import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  DollarSign, TrendingUp, Award, Users, Gift, Clock, 
  Star, ThumbsUp, ThumbsDown, Calendar, CheckCircle 
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export function PayrollTab({ user, selectedHotelId }) {
  const [staffList, setStaffList] = useState([]);
  const [managersList, setManagersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [bonusDialog, setBonusDialog] = useState(false);
  const [bonusAmount, setBonusAmount] = useState('');
  const [bonusReason, setBonusReason] = useState('');
  const [payrollDialog, setPayrollDialog] = useState(false);
  const [feedbackDialog, setFeedbackDialog] = useState(false);
  const [feedbackType, setFeedbackType] = useState('positive');
  const [feedbackText, setFeedbackText] = useState('');
  const [payrollConfig, setPayrollConfig] = useState({
    pay_type: 'fixed',
    pay_amount: 0,
    pay_schedule: 'monthly',
    bonus_eligible: true
  });

  const isOwner = user?.role === 'owner' || user?.role === 'admin';
  const isManager = user?.role === 'manager';

  useEffect(() => {
    loadPayrollData();
  }, [selectedHotelId]);

  const loadPayrollData = async () => {
    try {
      setLoading(true);
      const backendUrl = BACKEND_URL;

      if (isOwner) {
        // Owner sees both managers and staff
        const [managersRes, staffRes] = await Promise.all([
          fetch(`${backendUrl}/api/payroll/hotel/${selectedHotelId}/managers`),
          fetch(`${backendUrl}/api/payroll/hotel/${selectedHotelId}/staff`)
        ]);
        const managersData = await managersRes.json();
        const staffData = await staffRes.json();

        if (managersData.success) setManagersList(managersData.managers || []);
        if (staffData.success) setStaffList(staffData.staff || []);
      } else if (isManager) {
        // Manager sees only staff
        const response = await fetch(`${backendUrl}/api/payroll/hotel/${selectedHotelId}/staff`);
        const data = await response.json();
        if (data.success) setStaffList(data.staff || []);
      }
    } catch (error) {
      console.error('Failed to load payroll data:', error);
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const openBonusDialog = (person) => {
    setSelectedPerson(person);
    setBonusAmount('');
    setBonusReason('');
    setBonusDialog(true);
  };

  const openPayrollDialog = (person) => {
    setSelectedPerson(person);
    setPayrollConfig(person.payroll || {
      pay_type: 'fixed',
      pay_amount: 0,
      pay_schedule: 'monthly',
      bonus_eligible: true
    });
    setPayrollDialog(true);
  };

  const openFeedbackDialog = (person) => {
    setSelectedPerson(person);
    setFeedbackType('positive');
    setFeedbackText('');
    setFeedbackDialog(true);
  };

  const issueBonus = async () => {
    if (!bonusAmount || parseFloat(bonusAmount) <= 0) {
      toast.error('Please enter a valid bonus amount');
      return;
    }

    if (!bonusReason.trim()) {
      toast.error('Please enter a reason for the bonus');
      return;
    }

    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(
        `${backendUrl}/api/payroll/staff/${selectedPerson.id}/bonus?hotel_id=${selectedHotelId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: parseFloat(bonusAmount),
            reason: bonusReason
          })
        }
      );

      const data = await response.json();
      if (data.success) {
        toast.success(`Bonus of $${bonusAmount} issued to ${selectedPerson.name}!`);
        setBonusDialog(false);
      } else {
        toast.error(data.error || 'Failed to issue bonus');
      }
    } catch (error) {
      console.error('Failed to issue bonus:', error);
      toast.error('Failed to issue bonus');
    }
  };

  const updatePayroll = async () => {
    if (payrollConfig.pay_amount <= 0) {
      toast.error('Please enter a valid pay amount');
      return;
    }

    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(
        `${backendUrl}/api/payroll/staff/${selectedPerson.id}/config`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payrollConfig)
        }
      );

      const data = await response.json();
      if (data.success) {
        toast.success('Payroll updated successfully');
        setPayrollDialog(false);
        loadPayrollData();
      } else {
        toast.error(data.error || 'Failed to update payroll');
      }
    } catch (error) {
      console.error('Failed to update payroll:', error);
      toast.error('Failed to update payroll');
    }
  };

  const submitFeedback = async () => {
    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(
        `${backendUrl}/api/hotels/${selectedHotelId}/guest-feedback`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            staff_id: selectedPerson.id,
            is_positive: feedbackType === 'positive',
            feedback_text: feedbackText
          })
        }
      );

      const data = await response.json();
      if (data.success) {
        toast.success(`${feedbackType === 'positive' ? 'Positive' : 'Negative'} feedback logged for ${selectedPerson.name}`);
        setFeedbackDialog(false);
        loadPayrollData(); // Reload to get updated scores
      } else {
        toast.error(data.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback');
    }
  };

  const getScoreBadge = (score) => {
    const scoreNum = score?.total_score || 0;
    if (scoreNum >= 80) return <Badge variant="default" className="bg-green-600">Excellent ({scoreNum})</Badge>;
    if (scoreNum >= 60) return <Badge variant="default" className="bg-blue-600">Good ({scoreNum})</Badge>;
    if (scoreNum >= 40) return <Badge variant="secondary">Fair ({scoreNum})</Badge>;
    return <Badge variant="destructive">Needs Improvement ({scoreNum})</Badge>;
  };

  const renderStaffTable = (list, showRole = false) => {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            {showRole && <TableHead>Role</TableHead>}
            <TableHead>Pay Type</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Score</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.map((person) => {
            const payroll = person.payroll || {};
            return (
              <TableRow key={person.id}>
                <TableCell>
                  <div>
                    <div className="font-medium">{person.name}</div>
                    <div className="text-sm text-muted-foreground">{person.email}</div>
                  </div>
                </TableCell>
                {showRole && (
                  <TableCell>
                    <Badge>{person.role || person.staff_type}</Badge>
                  </TableCell>
                )}
                <TableCell>
                  <Badge variant="outline">
                    {payroll.pay_type === 'fixed' ? 'Fixed Salary' : 'Hourly Rate'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="font-semibold">
                    ${payroll.pay_amount || 0}
                    {payroll.pay_type === 'hourly' && '/hr'}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    {getScoreBadge(person.score)}
                    <div className="text-xs text-muted-foreground">
                      {person.score?.event_count || 0} events (30 days)
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openPayrollDialog(person)}
                    >
                      <DollarSign className="w-3 h-3 mr-1" />
                      Edit Pay
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openFeedbackDialog(person)}
                    >
                      <Star className="w-3 h-3 mr-1" />
                      Rate
                    </Button>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => openBonusDialog(person)}
                    >
                      <Gift className="w-3 h-3 mr-1" />
                      Bonus
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-3">Loading payroll data...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold heading-font mb-2">Payroll & Performance</h2>
          <p className="text-muted-foreground">
            {isOwner && 'Manage staff and manager payroll, track performance scores, and issue bonuses.'}
            {isManager && 'Manage staff payroll, track performance scores, and issue bonuses.'}
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{staffList.length}</div>
                  <div className="text-sm text-muted-foreground">Staff Members</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {isOwner && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <Star className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{managersList.length}</div>
                    <div className="text-sm text-muted-foreground">Managers</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    {Math.round(staffList.reduce((acc, s) => acc + (s.score?.total_score || 0), 0) / staffList.length || 0)}
                  </div>
                  <div className="text-sm text-muted-foreground">Avg Score</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                  <Award className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    {staffList.filter(s => (s.score?.total_score || 0) >= 80).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Top Performers</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Staff & Manager Lists */}
        {isOwner ? (
          <Tabs defaultValue="staff">
            <TabsList>
              <TabsTrigger value="staff">
                <Users className="w-4 h-4 mr-2" />
                Staff ({staffList.length})
              </TabsTrigger>
              <TabsTrigger value="managers">
                <Star className="w-4 h-4 mr-2" />
                Managers ({managersList.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="staff">
              <Card>
                <CardHeader>
                  <CardTitle>Staff Payroll & Performance</CardTitle>
                  <CardDescription>Manage staff compensation and view performance scores</CardDescription>
                </CardHeader>
                <CardContent>
                  {staffList.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No staff members yet
                    </div>
                  ) : (
                    renderStaffTable(staffList, true)
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="managers">
              <Card>
                <CardHeader>
                  <CardTitle>Manager Payroll & Performance</CardTitle>
                  <CardDescription>Manage manager compensation and view performance scores</CardDescription>
                </CardHeader>
                <CardContent>
                  {managersList.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No managers assigned to this hotel
                    </div>
                  ) : (
                    renderStaffTable(managersList, false)
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Staff Payroll & Performance</CardTitle>
              <CardDescription>Manage staff compensation and view performance scores</CardDescription>
            </CardHeader>
            <CardContent>
              {staffList.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No staff members yet
                </div>
              ) : (
                renderStaffTable(staffList, true)
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bonus Dialog */}
      <Dialog open={bonusDialog} onOpenChange={setBonusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5" />
              Issue Bonus to {selectedPerson?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="bonus-amount">Bonus Amount *</Label>
              <Input
                id="bonus-amount"
                type="number"
                placeholder="100.00"
                value={bonusAmount}
                onChange={(e) => setBonusAmount(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="bonus-reason">Reason *</Label>
              <Input
                id="bonus-reason"
                placeholder="Excellent performance this month"
                value={bonusReason}
                onChange={(e) => setBonusReason(e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setBonusDialog(false)}>
                Cancel
              </Button>
              <Button onClick={issueBonus}>
                Issue Bonus
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payroll Config Dialog */}
      <Dialog open={payrollDialog} onOpenChange={setPayrollDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Payroll Configuration: {selectedPerson?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="pay-type">Pay Type *</Label>
              <Select 
                value={payrollConfig.pay_type} 
                onValueChange={(value) => setPayrollConfig({...payrollConfig, pay_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed Monthly Salary</SelectItem>
                  <SelectItem value="hourly">Hourly Rate</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="pay-amount">
                {payrollConfig.pay_type === 'fixed' ? 'Monthly Salary' : 'Hourly Rate'} *
              </Label>
              <Input
                id="pay-amount"
                type="number"
                placeholder={payrollConfig.pay_type === 'fixed' ? '3000' : '15'}
                value={payrollConfig.pay_amount}
                onChange={(e) => setPayrollConfig({...payrollConfig, pay_amount: parseFloat(e.target.value) || 0})}
              />
            </div>

            <div>
              <Label htmlFor="pay-schedule">Pay Schedule</Label>
              <Select 
                value={payrollConfig.pay_schedule} 
                onValueChange={(value) => setPayrollConfig({...payrollConfig, pay_schedule: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setPayrollDialog(false)}>
                Cancel
              </Button>
              <Button onClick={updatePayroll}>
                Update Payroll
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={feedbackDialog} onOpenChange={setFeedbackDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5" />
              Rate Staff: {selectedPerson?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="feedback-type">Feedback Type *</Label>
              <Select value={feedbackType} onValueChange={setFeedbackType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="positive">
                    <div className="flex items-center gap-2">
                      <ThumbsUp className="w-4 h-4 text-green-600" />
                      Positive Review (+10 points)
                    </div>
                  </SelectItem>
                  <SelectItem value="negative">
                    <div className="flex items-center gap-2">
                      <ThumbsDown className="w-4 h-4 text-red-600" />
                      Negative Review (-15 points)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="feedback-text">Feedback (Optional)</Label>
              <Input
                id="feedback-text"
                placeholder="Great customer service, very helpful..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
              />
            </div>
            <div className="bg-muted p-3 rounded-lg text-sm">
              <p className="text-muted-foreground">
                This will {feedbackType === 'positive' ? 'add 10 points' : 'deduct 15 points'} from {selectedPerson?.name}'s reputation score.
              </p>
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setFeedbackDialog(false)}>
                Cancel
              </Button>
              <Button onClick={submitFeedback} variant={feedbackType === 'positive' ? 'default' : 'destructive'}>
                Submit {feedbackType === 'positive' ? 'Positive' : 'Negative'} Review
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
